import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlarmClockOff, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Polls for tasks still marked "in progress" (someone forgot to end / clocked out /
// running 8h+) and pings the manager anywhere in the app so runaway sessions get
// caught the same day, not days later. Only managers opted-in (user.stuck_alerts) see it.
export function StuckAlert() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissedKey, setDismissedKey] = useState("");

  const enabled = !!user?.stuck_alerts;

  const { data } = useQuery({
    queryKey: ["stuck-tasks-alert"],
    queryFn: async () => (await api.get("/stuck-tasks")).data,
    refetchInterval: 60000,
    enabled,
  });

  if (!enabled) return null;
  const stuck = data?.stuck || [];
  if (!stuck.length) return null;

  // A stable signature of the current stuck set — if it changes, re-show the alert.
  const key = stuck.map((s) => `${s.clean_id}:${s.cleaner_id}:${s.role}`).sort().join("|");
  if (key === dismissedKey) return null;

  const onTimeline = location.pathname === "/manager/timeline";

  return (
    <div
      data-testid="stuck-alert-banner"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      <AlarmClockOff className="h-5 w-5 shrink-0 text-red-600" />
      <span className="min-w-0 flex-1 font-medium">
        {stuck.length}{" "}
        {stuck.length > 1 ? t("tasks are still marked \u201Cin progress\u201D") : t("task is still marked \u201Cin progress\u201D")}
        {" \u2014 "}
        {t("someone may have forgotten to end it. End it so their hours stay correct.")}
      </span>
      {!onTimeline && (
        <Button
          size="sm"
          variant="destructive"
          data-testid="stuck-alert-review-btn"
          onClick={() => navigate("/manager/timeline")}
          className="shrink-0 gap-1.5"
        >
          <AlarmClockOff className="h-4 w-4" /> {t("Review on Timeline")}
        </Button>
      )}
      <button
        type="button"
        data-testid="stuck-alert-dismiss"
        onClick={() => setDismissedKey(key)}
        className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-100"
        aria-label={t("Dismiss")}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
